import { ListLibrariesQuery, Maybe, useListLibrariesQuery } from 'src/graphql';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TLibrary, TLibraryChooserProps } from './LibraryChooser.types';

import { AssetsListWithSearch } from 'src/components/AssetsListWithSearch';
import { LIBRARY_FILTERS_MAPPING } from 'src/constants/application';
import { NN } from 'src/typings/helpers';
import { TAssetType } from 'src/typings/base-types';
import { TBaseIconProps } from 'src/components/BaseIcon';
import { debounce } from 'lodash';
import { formatter } from 'src/utils/formatter';
import { produce } from 'immer';
import { useGraphQLMessage } from 'src/hooks/useGraphQLMessage';
import { v4 } from 'uuid';

const customHouseUuid = v4();
const customHouse: TLibrary = {
  libraryUuid: customHouseUuid,
  uuid: customHouseUuid,
  icon: 'house-3d',
  title: 'Custom House',
  subtitle: formatSubtitle(0, 0),
  type: 'Area',
  key: customHouseUuid,
  isCustomHouse: true,
  description: 'Add Custom House',
};

function formatSubtitle(
  areaCountNumber: Maybe<number> | undefined,
  deviceCountNumber: Maybe<number> | undefined,
): string {
  return `${
    typeof areaCountNumber === 'number'
      ? `Markets: ${areaCountNumber}`
      : ''
  } ${
    typeof deviceCountNumber === 'number'
      ? `Assets: ${deviceCountNumber}`
      : ''
  }`.trim();
}

function defaultIcons(type?: TAssetType | undefined) {
  switch (type) {
    case 'Area':
      return 'house-3d';
    case 'PV':
      return 'solar-3d';
    case 'Load':
      return 'plug-3d';
    case 'Storage':
      return 'battery-3d';
    default:
      return 'multi-party-house-3d';
  }
}

// TODO: - temporary solution, ask backend team to modify the endpoint
// important - do not change names
function generateIcon(
  name: string | null | undefined,
  type?: TAssetType | undefined,
): TBaseIconProps['icon'] {
  switch (name) {
    case '6 apartments building+PV':
      return 'multi-party-house-3d';
    case '6 apartments building':
      return 'multi-party-house-3d';
    case 'Flatsharing Student':
      return 'house-3d';
    case 'Retired couple':
      return 'house-3d';
    case 'Multigenerational house':
      return 'house-3d';
    case 'Young Couple':
      return 'house-3d';
    case 'Family 3 children+PV+Batt':
      return 'multi-party-house-3d';
    case 'Family 2 children':
      return 'multi-party-house-3d';
    case 'Family 2 children+PV':
      return 'multi-party-house-3d';
    case 'DefaultLib PV Berlin':
      return 'solar-3d';
    case 'DefaultLib Load':
      return 'plug-3d';
    case 'DefaultLib Storage':
      return 'battery-3d';
    default:
      return defaultIcons(type);
  }
}

export const LibraryChooser: React.FC<TLibraryChooserProps> = ({
  onLibraryChoose,
  ...rest
}: TLibraryChooserProps) => {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchVariable, setSearchVariable] = useState(''); // We use this second state to achieve search debouncing

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setSearchVariableDebounced = useCallback(
    debounce((v: typeof searchVariable) => setSearchVariable(v), 300),
    [],
  );

  const { data: queriedData, loading: loadingA, error: errorA } = useListLibrariesQuery({
    variables: {
      search: searchVariable,
      tags: rest.activeFilter,
    },
  });

  const { data: houseTypeData, loading: loadingB, error: errorB } = useListLibrariesQuery({
    variables: {
      tags: LIBRARY_FILTERS_MAPPING.Area,
    },
  });

  const filteredAssets = useMemo(() => {
    if (!queriedData || !houseTypeData) return [];

    if (
      rest.activeFilter === LIBRARY_FILTERS_MAPPING.All &&
      rest.disabledFilters?.includes(LIBRARY_FILTERS_MAPPING.Area)
    ) {
      const output: NN<ListLibrariesQuery['listLibraries']>['libraries'] = [];
      const houses = houseTypeData.listLibraries?.libraries?.map((p) => p?.uuid) || [];

      queriedData.listLibraries?.libraries?.map((item) => {
        if (houses.includes(item?.uuid)) return;
        output.push(item);
      });

      return output;
    }

    return queriedData.listLibraries?.libraries || [];
  }, [queriedData, houseTypeData, rest.disabledFilters, rest.activeFilter]);

  const mappedAssets = useMemo((): TLibrary[] => {
    return filteredAssets.map((item) => {
      const { areaCountNumber, deviceCountNumber, representation } = item?.scenarioData || {};

      const libraryUuid = item?.uuid || '';

      const library: TLibrary = {
        libraryUuid,
        uuid: representation?.uuid || '',
        icon: generateIcon(item?.name, representation?.type as TAssetType),
        title: item?.name || '',
        subtitle: formatSubtitle(areaCountNumber, deviceCountNumber),
        description: item?.description,
        type: representation?.type as TAssetType,
        scenarioData: item?.scenarioData,
        key: libraryUuid,
      };

      return library;
    });
  }, [filteredAssets]);

  const mappedAssetsExtended = useMemo(() => {
    if (
      searchVariable &&
      !customHouse.title.toLowerCase().includes(searchVariable.trim().toLowerCase())
    ) {
      return mappedAssets;
    }

    if (
      (rest.activeFilter === LIBRARY_FILTERS_MAPPING.All &&
        !rest.disabledFilters?.includes(LIBRARY_FILTERS_MAPPING.Area)) ||
      rest.activeFilter === LIBRARY_FILTERS_MAPPING.Area
    ) {
      // Append "Custom House" asset
      return produce(mappedAssets, (draftState) => {
        draftState.splice(0, 0, customHouse);
      });
    }

    return mappedAssets;
  }, [mappedAssets, rest.activeFilter, rest.disabledFilters, searchVariable]);

  useEffect(() => {
    setSearchVariableDebounced(searchInputValue);
  }, [searchInputValue, setSearchVariableDebounced]);

  const errorComposed = mappedAssetsExtended ? undefined : (errorA || errorB); // prettier-ignore
  const errorFormatted = useGraphQLMessage({ error: errorComposed });

  return (
    <AssetsListWithSearch
      assets={mappedAssetsExtended}
      searchQuery={searchInputValue}
      onSearchQueryChange={(v) => setSearchInputValue(v)}
      onAssetChoose={onLibraryChoose}
      loading={loadingA || loadingB}
      error={errorFormatted}
      {...rest}
    />
  );
};
